import React from 'react';
import { cn } from '../lib/utils';
import { ShoppingBag, Layers, Menu, Trophy, MessageSquareText, Sparkles, Check, Star } from 'lucide-react';

export type FeatureIconType = 'calendar' | 'checklist' | 'ticket' | 'shop' | 'menu' | 'play' | 'trophy' | 'altar' | 'news' | 'milestone' | 'special';

interface FeatureIconProps {
  type: FeatureIconType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function FeatureIcon({ type, size = 'md', className }: FeatureIconProps) {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={cn("relative shrink-0", sizeClasses[size], className)}>
      {type === 'calendar' ? (
        /* 3D Tactile Calendar */
        <div className="w-full h-full relative flex items-center justify-center">
          {/* Back Shadow for thickness */}
          <div className="absolute top-[18%] inset-x-[15%] bottom-[12%] bg-slate-300 rounded-lg shadow-md z-0" />
          
          {/* Calendar Body */}
          <div className="absolute top-[14%] inset-x-[10%] bottom-[16%] bg-white rounded-lg shadow-sm border border-slate-200 z-10 flex flex-col overflow-hidden">
            {/* Red Header */}
            <div className="h-[25%] w-full bg-gradient-to-b from-rose-500 to-rose-600 border-b border-rose-400/50 flex justify-around items-end pb-[2%] px-[15%]">
              {/* Binder Holes (Dark) */}
              <div className="w-[12%] aspect-square bg-slate-800 rounded-full" />
              <div className="w-[12%] aspect-square bg-slate-800 rounded-full" />
            </div>
            
            {/* Grid */}
            <div className="flex-1 w-full p-[12%] grid grid-cols-4 grid-rows-3 gap-[8%]">
               {[...Array(12)].map((_, i) => (
                 <div key={i} className={cn(
                   "w-full h-full rounded-[1px]",
                   i === 6 ? "bg-rose-400" : "bg-slate-200"
                 )} />
               ))}
            </div>
          </div>
          
          {/* Silver Binder Rings */}
          <div className="absolute top-[8%] left-[22%] w-[12%] h-[18%] bg-gradient-to-b from-slate-100 to-slate-400 border border-slate-300 rounded-full shadow-sm z-20" />
          <div className="absolute top-[8%] right-[22%] w-[12%] h-[18%] bg-gradient-to-b from-slate-100 to-slate-400 border border-slate-300 rounded-full shadow-sm z-20" />
        </div>
      ) : type === 'checklist' ? (
        /* 3D Proportional Tactile Mission Clipboard */
        <div className="w-full h-full relative flex items-center justify-center">
          {/* Board Backing */}
          <div className="absolute inset-x-[14%] top-[14%] bottom-[8%] bg-gradient-to-b from-[#b45309] to-[#78350f] rounded-[4px] border border-[#92400e]/30 shadow-sm z-0" />
          
          {/* Paper Sheet */}
          <div className="absolute inset-x-[20%] top-[24%] bottom-[14%] bg-gradient-to-b from-white to-slate-50 rounded-[2px] shadow-xs p-[8%] flex flex-col justify-between z-10">
            {/* Checklist Rows */}
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-[10%] h-[20%] w-full">
                {/* Green Checked Checkbox */}
                <div className="w-[20%] h-full bg-emerald-500 rounded-[1px] flex items-center justify-center shrink-0">
                  <div className="w-[60%] h-[60%] bg-white rounded-[0.5px]" />
                </div>
                {/* Line text */}
                <div className={cn("h-[20%] bg-slate-300 rounded-full", i === 0 ? "w-[70%]" : i === 1 ? "w-[55%]" : "w-[40%]")} />
              </div>
            ))}
          </div>

          {/* Metal Clamp */}
          <div className="absolute top-[8%] left-[32%] right-[32%] h-[12%] bg-gradient-to-b from-slate-300 to-slate-400 rounded-sm border border-slate-200 z-20 shadow-xs" />
        </div>
      ) : type === 'shop' ? (
        /* 3D Tactile Shopfront */
        <div className="w-full h-full relative flex items-center justify-center">
          {/* Awning/Roof */}
          <div className="absolute top-[10%] inset-x-[10%] h-[30%] bg-gradient-to-r from-rose-500 via-rose-400 to-rose-600 rounded-t-lg shadow-sm border border-rose-300/30 z-20 overflow-hidden flex">
            {[...Array(5)].map((_, i) => (
              <div key={i} className={cn("flex-1 h-full", i % 2 === 0 ? "bg-rose-500" : "bg-white/90")} />
            ))}
          </div>
          {/* Awning Scallops */}
          <div className="absolute top-[36%] inset-x-[10%] h-[8%] flex z-20">
            {[...Array(5)].map((_, i) => (
              <div key={i} className={cn("flex-1 h-full rounded-b-full shadow-sm", i % 2 === 0 ? "bg-rose-600" : "bg-white/80")} />
            ))}
          </div>
          {/* Shop Body */}
          <div className="absolute top-[38%] inset-x-[15%] h-[48%] bg-gradient-to-b from-rose-100 to-rose-200 rounded-b-lg border border-rose-300/30 shadow-md z-10">
            {/* Glass Window/Door */}
            <div className="absolute inset-x-[20%] top-[20%] bottom-0 bg-gradient-to-tr from-cyan-300/70 to-cyan-100/50 rounded-t-md border border-cyan-200/50 overflow-hidden">
              <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-gradient-to-br from-white/30 to-transparent rotate-12" />
            </div>
            {/* Small Sign */}
            <div className="absolute top-[5%] left-[5%] w-2 h-2 bg-yellow-400 rounded-sm shadow-sm rotate-45" />
          </div>
        </div>
      ) : type === 'menu' ? (
        /* 3D Tactile Hamburger Menu */
        <div className="w-full h-full relative flex flex-col justify-center items-center gap-[12%] p-[10%]">
          {[...Array(3)].map((_, i) => (
            <div 
              key={i} 
              className="w-full h-[18%] bg-gradient-to-r from-sky-400 via-sky-300 to-sky-500 rounded-full border border-sky-300/30 shadow-[0_2px_4px_rgba(0,0,0,0.15)] relative overflow-hidden"
              style={{
                transform: `translateZ(${10 - i * 3}px)`
              }}
            >
              <div className="absolute top-[10%] inset-x-2 h-[20%] bg-white/40 rounded-full blur-[0.5px]" />
              <div className="absolute bottom-0 inset-x-0 h-[25%] bg-black/15" />
            </div>
          ))}
        </div>
      ) : type === 'play' ? (
        /* 3D Tactile Play Button */
        <div className="w-full h-full relative flex items-center justify-center">
          <div className="absolute inset-[8%] bg-gradient-to-b from-yellow-300 via-yellow-400 to-amber-500 rounded-full border border-yellow-200/50 shadow-[inset_0_4px_8px_rgba(255,255,255,0.6),_inset_0_-4px_8px_rgba(0,0,0,0.15)] flex items-center justify-center z-10">
            <div className="w-0 h-0 border-t-[14px] border-t-transparent border-l-[24px] border-l-white border-b-[14px] border-b-transparent relative drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)] ml-1">
              <div className="absolute inset-0 border-l-[24px] border-l-white/30" style={{ clipPath: 'polygon(0 0, 100% 50%, 0 50%)' }} />
            </div>
          </div>
        </div>
      ) : type === 'trophy' ? (
        /* 3D Tactile Golden Trophy */
        <div className="w-full h-full relative flex items-center justify-center">
          <div className="w-full h-full relative flex items-center justify-center">
            <div className="absolute top-[22%] left-[18%] w-[20%] h-[30%] rounded-full border-4 border-amber-400 bg-transparent rotate-[-15deg] z-0" />
            <div className="absolute top-[22%] right-[18%] w-[20%] h-[30%] rounded-full border-4 border-amber-400 bg-transparent rotate-[15deg] z-0" />
            <div className="absolute bottom-[10%] inset-x-[28%] h-[12%] bg-gradient-to-b from-slate-600 to-slate-800 rounded-md border border-slate-500/50 shadow-md z-10" />
            <div className="absolute bottom-[20%] left-1/2 -translate-x-1/2 w-[16%] h-[20%] bg-gradient-to-b from-amber-500 to-amber-700 border-x border-amber-600/50 z-10" />
            <div className="absolute top-[18%] inset-x-[26%] h-[48%] bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 rounded-t-2xl rounded-b-[40%] border border-amber-200/50 shadow-md overflow-hidden z-20">
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/15 to-transparent" />
              <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[40%] h-[40%] bg-white/20 rounded-full flex items-center justify-center">
                <Star className="w-[70%] h-[70%] text-amber-100 fill-amber-200" />
              </div>
            </div>
          </div>
        </div>
      ) : type === 'altar' ? (
        /* 3D Tactile Atom (Prism Altar) - Refined to match user image */
        <div className="w-full h-full relative flex items-center justify-center">
           <div className="relative w-full h-full flex items-center justify-center">
              {/* Orbiting Paths */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full p-0.5">
                <ellipse cx="50" cy="50" rx="42" ry="15" fill="none" stroke="currentColor" strokeWidth="6" transform="rotate(0 50 50)" className="opacity-40" />
                <ellipse cx="50" cy="50" rx="42" ry="15" fill="none" stroke="currentColor" strokeWidth="6" transform="rotate(60 50 50)" className="opacity-40" />
                <ellipse cx="50" cy="50" rx="42" ry="15" fill="none" stroke="currentColor" strokeWidth="6" transform="rotate(120 50 50)" className="opacity-40" />
              </svg>
              
              {/* Nucleus - Polished 3D Sphere */}
              <div className="relative w-5 h-5 flex items-center justify-center">
                 <div className="absolute inset-0 bg-white rounded-full shadow-[0_2px_10px_rgba(255,255,255,0.8)] z-10">
                    {/* Top Highlight */}
                    <div className="absolute top-0.5 left-1 w-2 h-1.5 bg-white rounded-full blur-[1px] opacity-90" />
                    {/* Inner Core Color (Subtle) */}
                    <div className="absolute inset-0.5 bg-gradient-to-br from-white via-cyan-100 to-cyan-300 rounded-full" />
                 </div>
                 {/* Outer Glow */}
                 <div className="absolute inset-0 bg-white/40 rounded-full blur-sm animate-pulse" />
              </div>
           </div>
        </div>
      ) : type === 'news' ? (
        /* 3D Tactile Speech Bubble / Newspaper */
        <div className="w-full h-full relative flex items-center justify-center">
          {/* Main Bubble */}
          <div className="absolute inset-[15%] bg-gradient-to-br from-white to-slate-100 rounded-xl border border-slate-200 shadow-[0_4px_8px_rgba(0,0,0,0.15)] z-10 flex flex-col p-[12%]">
            {/* Headline */}
            <div className="w-[70%] h-[15%] bg-slate-400 rounded-sm mb-[15%]" />
            {/* Text lines */}
            <div className="flex flex-col gap-[15%] w-full h-[50%]">
              <div className="w-full h-[25%] bg-slate-300 rounded-sm" />
              <div className="w-[85%] h-[25%] bg-slate-300 rounded-sm" />
              <div className="w-[60%] h-[25%] bg-slate-300 rounded-sm" />
            </div>
          </div>
          {/* Bubble Tail */}
          <div className="absolute bottom-[5%] right-[25%] w-[25%] h-[25%] bg-slate-100 border-b border-r border-slate-200 shadow-[2px_2px_4px_rgba(0,0,0,0.1)] rotate-45 z-0" />
        </div>
      ) : type === 'milestone' ? (
        /* 3D Proportional Tactile Milestone Bundle / Gift Box */
        <div className="w-full h-full relative flex items-center justify-center">
          {/* Box Lid - 3D Tactile Cap */}
          <div className="absolute top-[15%] inset-x-[12%] h-[18%] bg-gradient-to-r from-purple-400 via-purple-50 to-purple-600 rounded-[4px] shadow-sm border border-purple-300/30 z-20" />
          
          {/* Box Body - 3D Tactile Base */}
          <div className="absolute top-[31%] inset-x-[16%] h-[53%] bg-gradient-to-br from-purple-500 via-purple-650 to-indigo-700 rounded-b-xl border border-purple-400/30 shadow-md overflow-hidden z-10">
            {/* Highlight gradient */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
          </div>

          {/* Vertical Ribbon (Gold) */}
          <div className="absolute top-[15%] bottom-[17%] left-[44%] w-[12%] bg-gradient-to-b from-amber-300 via-amber-400 to-amber-500 border border-amber-200/50 shadow-sm z-30" />

          {/* Horizontal Ribbon (Gold) */}
          <div className="absolute top-[48%] inset-x-[16%] h-[12%] bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 border border-amber-200/50 shadow-sm z-30" />

          {/* Ribbon Bow Loops */}
          <div className="absolute top-[3%] left-[26%] w-[24%] h-[20%] bg-gradient-to-br from-amber-300 to-amber-400 rounded-full border border-amber-200/50 shadow-sm rotate-[-25deg] z-30" />
          <div className="absolute top-[3%] right-[26%] w-[24%] h-[20%] bg-gradient-to-bl from-amber-300 to-amber-400 rounded-full border border-amber-200/50 shadow-sm rotate-[25deg] z-30" />

          {/* Center Lock / Badge - Gold Star */}
          <div className="absolute top-[43%] left-1/2 -translate-x-1/2 w-[36%] h-[36%] bg-gradient-to-br from-white to-slate-100 rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.15)] flex items-center justify-center border border-white/80 z-40">
            <Sparkles className="w-[75%] h-[75%] text-purple-600 fill-purple-400" />
          </div>
          
          {/* Outer Glow Sparkles */}
          <Sparkles className="absolute -top-0.5 -right-0.5 w-[30%] h-[30%] text-amber-300 animate-pulse z-40" />
        </div>
      ) : type === 'special' ? (
        /* 3D Tactile Special Badge */
        <div className="w-full h-full relative flex items-center justify-center">
          {/* Base Diamond */}
          <div className="absolute top-[10%] bottom-[10%] left-[10%] right-[10%] bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-600 rounded-md border border-amber-200/50 shadow-md rotate-45 z-10 overflow-hidden">
             {/* Glossy Top */}
             <div className="absolute top-0 left-0 w-[150%] h-1/2 bg-white/30 -rotate-12" />
          </div>
          {/* Inner Raised Diamond */}
          <div className="absolute top-[20%] bottom-[20%] left-[20%] right-[20%] bg-gradient-to-br from-yellow-100 via-amber-300 to-amber-500 rounded-sm shadow-inner rotate-45 z-20 flex items-center justify-center border border-white/40">
             {/* Star inside rotated back */}
             <Star className="w-[90%] h-[90%] text-amber-50 fill-white/90 -rotate-45 drop-shadow-sm" />
          </div>
          {/* Sparkle Accent */}
          <Sparkles className="absolute top-0 right-0 w-[35%] h-[35%] text-yellow-200 animate-pulse z-30 drop-shadow-sm" />
        </div>
      ) : (
        /* 3D Tactile Ticket */
        <div className="w-full h-full relative">
           <div className="absolute inset-0 bg-gradient-to-br from-rose-500 to-rose-600 shadow-md" style={{ clipPath: 'polygon(10% 0, 90% 0, 100% 50%, 90% 100%, 10% 100%, 0 50%)' }} />
           <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '4px 100%' }} />
           <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white/20 rounded-full border border-white/40" />
        </div>
      )}
    </div>
  );
}
